import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const CYAN = [0, 229, 255];
const INK = [5, 5, 5];
const MUTE = [150, 150, 155];

function drawHeader(doc, W, title, subtitle, meta) {
  doc.setFillColor(...INK);
  doc.rect(0, 0, W, 96, "F");
  // cyan accent bar
  doc.setFillColor(...CYAN);
  doc.rect(0, 96, W, 3, "F");
  // logo mark
  doc.setFillColor(...CYAN);
  doc.circle(40, 46, 12, "F");
  doc.setTextColor(5, 5, 5);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("O", 40, 50, { align: "center" });
  // wordmark
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("OCTON", 62, 42);
  doc.setTextColor(...CYAN);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("FOOTBALL COACH COMPANION · AVA FLEET", 62, 55);
  // title
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text((title || "Analysis Report").toUpperCase(), 40, 80);
  if (subtitle) {
    doc.setTextColor(...MUTE);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(subtitle, W - 40, 42, { align: "right" });
  }
  const stamp = new Date().toLocaleString();
  doc.setTextColor(...MUTE);
  doc.setFontSize(8);
  doc.text(`Generated ${stamp}`, W - 40, 55, { align: "right" });
  if (meta && meta.length) {
    doc.setFontSize(8);
    doc.text(meta.join("   ·   "), W - 40, 80, { align: "right" });
  }
}

function drawFooter(doc) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setDrawColor(40, 40, 40);
    doc.line(40, H - 28, W - 40, H - 28);
    doc.setTextColor(...MUTE);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.text("OCTON · Hebbian + Bayesian super-brain · trained on real match data", 40, H - 16);
    doc.text(`${i} / ${pages}`, W - 40, H - 16, { align: "right" });
  }
}

// Capture a live DOM node (charts, bars, colours) into a branded, paginated PDF.
// output: "save" (download) | "base64" (return the PDF bytes as base64 for emailing)
export async function exportAnalysisPdf({ node, title, subtitle, meta = [], filename, output = "save" }) {
  const doc = new jsPDF({ unit: "pt", format: "a4", compress: true });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  drawHeader(doc, W, title, subtitle, meta);

  const top = 116;
  if (node) {
    const canvas = await html2canvas(node, { backgroundColor: "#070707", scale: output === "base64" ? 1.5 : 2, useCORS: true, logging: false });
    const imgW = W - 64;
    const imgH = (canvas.height * imgW) / canvas.width;
    const img = canvas.toDataURL("image/jpeg", output === "base64" ? 0.8 : 0.9);
    let position = top;
    doc.addImage(img, "JPEG", 32, position, imgW, imgH);
    let heightLeft = imgH - (H - top - 36);
    while (heightLeft > 0) {
      doc.addPage();
      position = 24 - (imgH - heightLeft);
      doc.addImage(img, "JPEG", 32, position, imgW, imgH);
      heightLeft -= (H - 24 - 36);
    }
  }
  drawFooter(doc);
  if (output === "base64") return doc.output("datauristring").split(",")[1];
  doc.save(filename || "octon-report.pdf");
}

// Text/structured PDF (e.g. AVA voice-live summary) — branded, no DOM capture.
export function exportTextPdf({ title, subtitle, meta = [], sections = [], filename }) {
  const doc = new jsPDF({ unit: "pt", format: "a4", compress: true });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  drawHeader(doc, W, title, subtitle, meta);
  let y = 128;
  const maxW = W - 80;
  const ensure = (h) => { if (y + h > H - 44) { doc.addPage(); y = 48; } };
  sections.forEach((s) => {
    ensure(28);
    doc.setTextColor(...CYAN);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text((s.heading || "").toUpperCase(), 40, y);
    y += 16;
    if (s.body) {
      doc.setTextColor(230, 230, 230);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10.5);
      const lines = doc.splitTextToSize(s.body, maxW);
      lines.forEach((ln) => { ensure(15); doc.text(ln, 40, y); y += 15; });
    }
    (s.bullets || []).forEach((b) => {
      doc.setTextColor(230, 230, 230);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10.5);
      const lines = doc.splitTextToSize(b, maxW - 14);
      ensure(15);
      doc.setTextColor(...CYAN);
      doc.text("›", 40, y);
      doc.setTextColor(230, 230, 230);
      lines.forEach((ln, i) => { ensure(15); doc.text(ln, 54, y); y += 15; });
    });
    y += 12;
  });
  drawFooter(doc);
  doc.save(filename || "octon-ava-summary.pdf");
}
